import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { Layout } from "./components/Layout";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { useEffect, useState } from "react";
import OnboardingBackfill from "./components/OnboardingBackfill";
import axios from "axios";

// Pages
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Nutrition from "./pages/Nutrition";
import Sleep from "./pages/Sleep";
import Fitness from "./pages/Fitness";
import Tasks from "./pages/Tasks";
import Travel from "./pages/Travel";
import Coach from "./pages/Coach";
import Achievements from "./pages/Achievements";
import Settings from "./pages/Settings";
import Health from "./pages/Health";
import Profile from "./pages/Profile";
import TrustSafety from "./pages/TrustSafety";
import WearableIntegration from "./pages/WearableIntegration";
import MeetNanouk from "./pages/MeetNanouk";
import Statistics from "./pages/Statistics";
import WeeklySummary from "./pages/WeeklySummary";
import Legal from "./pages/Legal";
import Terms from "./pages/Terms";
import GroundingPage from "./pages/GroundingPage";
import Blog from "./pages/Blog";
import BlogStudioPage from "./pages/BlogStudioPage";
import { Seo } from "./components/seo/Seo";
import History from "./pages/History";
import Goals from "./pages/Goals";
import Help from "./pages/Help";

const API = process.env.REACT_APP_BACKEND_URL;

// Register Service Worker
// Also wires up auto-update detection so users on iPhone PWA never get
// stranded on a stale bundle: when a new sw.js is found, we surface a
// "Update available — tap to reload" toast that calls skipWaiting + reload.
const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return;
    try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('🐺 BESTBUDDY Service Worker registered:', registration.scope);

        // If a new worker is already waiting when this page loaded, prompt now.
        const promptToUpdate = (worker) => {
            const { toast } = require('sonner');
            toast.info('Update verfügbar – tippe zum Neuladen', {
                duration: 30000,
                action: {
                    label: 'Neu laden',
                    onClick: () => {
                        worker.postMessage({ type: 'SKIP_WAITING' });
                    },
                },
                id: 'sw-update-available',
            });
        };

        if (registration.waiting) {
            promptToUpdate(registration.waiting);
        }

        // Listen for new workers becoming "installed" → ready to take over.
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;
            newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // A new SW is installed AND there's an existing controller
                    // → it's an update (not a fresh install). Prompt the user.
                    promptToUpdate(newWorker);
                }
            });
        });

        // When the new SW takes control, reload once so the page runs the
        // freshly cached bundle. Guarded so we don't loop on Android Chrome.
        let reloadedOnce = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (reloadedOnce) return;
            reloadedOnce = true;
            window.location.reload();
        });

        // Best-effort: ping the registration every 60 min in case the user
        // keeps the PWA open across many days without restarting it.
        setInterval(() => {
            registration.update().catch(() => {});
        }, 60 * 60 * 1000);
    } catch (error) {
        console.error('Service Worker registration failed:', error);
    }
};

// Detect new deployments even when sw.js itself didn't change: compare the
// main.<hash>.js bundle currently running against the one referenced by a
// freshly fetched index.html. Runs on start, on app resume and every 30 min.
const startBuildVersionWatcher = () => {
    const current = document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src');
    if (!current) return; // dev server has no hashed bundle
    let prompted = false;
    const check = async () => {
        if (prompted || document.visibilityState === 'hidden') return;
        try {
            const res = await fetch(`/index.html?ts=${Date.now()}`, { cache: 'no-store' });
            const html = await res.text();
            const m = html.match(/\/static\/js\/main\.[^"]+\.js/);
            if (m && m[0] !== current) {
                prompted = true;
                const { toast } = require('sonner');
                toast.info('Neue Version verfügbar 🐺', {
                    duration: Infinity,
                    action: { label: 'Jetzt aktualisieren', onClick: () => window.location.reload() },
                    id: 'build-update-available',
                });
            }
        } catch (_e) { /* offline — retry on next trigger */ }
    };
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
    });
    setTimeout(check, 10000);
    setInterval(check, 30 * 60 * 1000);
};

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const [needsBackfill, setNeedsBackfill] = useState(null);
    const [checkingBackfill, setCheckingBackfill] = useState(true);
    
    useEffect(() => {
        const checkBackfillStatus = async () => {
            if (user) {
                try {
                    const token = localStorage.getItem('wellness_token');
                    const response = await axios.get(`${API}/api/backfill/status`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setNeedsBackfill(!response.data.backfill_complete);
                } catch (error) {
                    console.error('Backfill check failed:', error);
                    setNeedsBackfill(false); // Skip on error
                }
            }
            setCheckingBackfill(false);
        };
        
        if (user) {
            checkBackfillStatus();
        } else {
            setCheckingBackfill(false);
        }
    }, [user]);
    
    if (loading || checkingBackfill) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-lime-500 via-indigo-500 to-orange-500 animate-pulse" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/auth" replace />;
    }
    
    // Show backfill onboarding for new users
    if (needsBackfill && localStorage.getItem('bestbuddy_needs_onboarding') !== 'true') {
        return (
            <OnboardingBackfill 
                onComplete={() => setNeedsBackfill(false)} 
                language="de"
            />
        );
    }
    
    return <Layout><Seo noindex />{children}</Layout>;
};

const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-lime-500 via-indigo-500 to-orange-500 animate-pulse" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

const HomeRoute = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-lime-500 via-indigo-500 to-orange-500 animate-pulse" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    // SEO: render the landing page directly on "/" (no redirect) so Google can index the root URL
    if (!user) {
        return <Auth />;
    }

    return <ProtectedRoute><Dashboard /></ProtectedRoute>;
};

// Forces English whenever the URL uses the /en prefix (TAAFT/SEO requirement)
const LangSync = () => {
    const location = useLocation();
    const { language, setLanguage } = useLanguage();
    useEffect(() => {
        const isEn = location.pathname === '/en' || location.pathname.startsWith('/en/');
        const stateLang = location.state?.lang;
        if (stateLang && stateLang !== language) setLanguage(stateLang);
        else if (isEn && language !== 'en') setLanguage('en');
    }, [location, language, setLanguage]);
    return null;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/nutrition" element={<ProtectedRoute><Nutrition /></ProtectedRoute>} />
            <Route path="/sleep" element={<ProtectedRoute><Sleep /></ProtectedRoute>} />
            <Route path="/fitness" element={<ProtectedRoute><Fitness /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/travel" element={<ProtectedRoute><Travel /></ProtectedRoute>} />
            <Route path="/health" element={<ProtectedRoute><Health /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/coach" element={<ProtectedRoute><Coach /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
            <Route path="/statistics" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
            <Route path="/weekly" element={<ProtectedRoute><WeeklySummary /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
            <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/trust" element={<ProtectedRoute><TrustSafety /></ProtectedRoute>} />
            <Route path="/wearables" element={<ProtectedRoute><WearableIntegration /></ProtectedRoute>} />
            <Route path="/meet-nanouk" element={<MeetNanouk />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<GroundingPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog-studio" element={<ProtectedRoute><BlogStudioPage /></ProtectedRoute>} />
            <Route path="/blog/:slug" element={<Blog />} />
            {/* English versions at separate URLs (/en prefix) */}
            <Route path="/en" element={<HomeRoute />} />
            <Route path="/en/blog" element={<Blog />} />
            <Route path="/en/blog/:slug" element={<Blog />} />
            <Route path="/en/legal" element={<Legal />} />
            <Route path="/en/terms" element={<Terms />} />
            <Route path="/en/about" element={<GroundingPage />} />
            <Route path="/en/meet-nanouk" element={<MeetNanouk />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    useEffect(() => {
        registerServiceWorker();
        startBuildVersionWatcher();
    }, []);

    return (
        <LanguageProvider>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <LangSync />
                        <AppRoutes />
                        <Toaster position="bottom-center" richColors toastOptions={{ style: { marginBottom: '5rem', fontSize: '13px', lineHeight: '1.4', maxWidth: '340px' } }} />
                        <PWAInstallPrompt />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </LanguageProvider>
    );
}

export default App;
